import { useNavigate } from 'react-router-dom'

import { readingListIndex, type ReadingListEntry } from '@/shared/lib/listOrder'

export type ReadingList = {
  /** Woher die Liste stammt - wandert als `from` mit, damit der Zurück-Knopf stimmt. */
  key: string
  entries: ReadingListEntry[]
}

export type ReadingListNav = {
  onBeyondPrev?: () => void
  onBeyondNext?: () => void
  listPosition: { index: number; total: number } | null
}

/**
 * Blättern durch die Liste, aus der man gekommen ist - über Kapitelgrenzen hinweg.
 *
 * Innerhalb eines Kapitels blättert der Player selbst von Artikel zu Artikel; er ruft
 * hier erst an, wenn er am Anfang oder Ende angekommen ist. Deshalb liegt das Ziel
 * immer in einem anderen Kapitel, und ein Wechsel der Route ist der richtige Weg.
 *
 * `read=true` öffnet die Leseansicht sofort, ohne Umweg über die Kapitelübersicht.
 */
export function useReadingListNav(
  list: ReadingList | undefined,
  chapterSlug: string | null | undefined,
  articleSlug: string | null
): ReadingListNav | null {
  const navigate = useNavigate()

  if (!list || list.entries.length < 2) return null

  const index = readingListIndex(list.entries, chapterSlug ?? null, articleSlug)
  if (index < 0) return null

  const go = (offset: -1 | 1) => () => {
    const target = list.entries[index + offset]
    if (!target) return
    const params = new URLSearchParams({ from: list.key, read: 'true' })
    if (target.articleSlug) params.set('start', target.articleSlug)
    navigate(`/user/chapter/${target.chapterSlug}?${params}`)
  }

  return {
    onBeyondPrev: index > 0 ? go(-1) : undefined,
    onBeyondNext: index < list.entries.length - 1 ? go(1) : undefined,
    listPosition: { index: index + 1, total: list.entries.length },
  }
}
